import type { MaybeRefOrGetter, Ref } from 'vue'

export interface UseInlineEditableFieldOptions {
  fromDraft: (draft: string) => string
  isEqual: (left: string, right: string) => boolean
  onCommit: (value: string) => void
  resetValidation?: () => void
  sourceValue: MaybeRefOrGetter<string>
  toDraft: (value: string) => string
  validate: () => Promise<boolean>
}

export interface UseInlineEditableFieldReturn {
  cancelEditing: () => void
  draft: Ref<string>
  handleCommit: () => Promise<void>
  handleKeydown: (event: KeyboardEvent) => void
  isEditing: Ref<boolean>
  revert: () => void
  startEditing: (preserveValidation?: boolean) => void
  stopEditing: () => void
}

export const useInlineEditableField = (options: UseInlineEditableFieldOptions): UseInlineEditableFieldReturn => {
  const source = () => toValue(options.sourceValue)

  const isEditing = ref(source() === '')
  const draft = ref(options.toDraft(source()))

  const revert = () => {
    draft.value = options.toDraft(source())
  }

  const stopEditing = () => {
    isEditing.value = false
  }

  const startEditing = (preserveValidation = false) => {
    if (isEditing.value) {
      return
    }

    draft.value = options.toDraft(source())
    if (!preserveValidation) {
      options.resetValidation?.()
    }
    isEditing.value = true
  }

  const cancelEditing = () => {
    revert()
    options.resetValidation?.()
    stopEditing()
  }

  watch(
    () => source(),
    (value) => {
      if (isEditing.value) {
        return
      }

      draft.value = options.toDraft(value)
      isEditing.value = value === ''
    },
  )

  const handleCommit = async () => {
    const currentValue = source()
    const nextValue = options.fromDraft(draft.value)

    if (options.isEqual(nextValue, currentValue)) {
      stopEditing()
      return
    }

    const isValid = await options.validate()
    if (!isValid) {
      isEditing.value = true
      return
    }

    options.onCommit(nextValue)
    stopEditing()
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditing()
      return
    }

    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    void handleCommit()
  }

  return {
    cancelEditing,
    draft,
    handleCommit,
    handleKeydown,
    isEditing,
    revert,
    startEditing,
    stopEditing,
  }
}
